/**
 * Last Purchase Rate.
 *
 * Every time the Head of Procurement approves a purchase order, the rate on
 * each line is filed away against (material, site, vendor). This screen is
 * that memory — what we last agreed to pay, to whom, for where, and how long
 * ago.
 *
 * It is a buying tool, not an accounting one. The figure it shows is the rate
 * we committed to on the order, not the rate we were eventually invoiced —
 * that difference is a payables question and lives on the payable screen.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, IndianRupee } from 'lucide-react'
import {
  Card, EmptyState, Loading, SearchInput, Select, Stat, Th, sortRows, useSort,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useQuery, useSites, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { can } from '@/lib/domain'
import { ddmmyyyy, qty, rupees, localIso } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'

export function Rates() {
  const { profile } = useAuth()
  const role = profile!.role

  const [site, setSite] = useState('')
  const [vendor, setVendor] = useState('')
  const [term, setTerm] = useState('')

  const { data: sites } = useSites()
  const { data: vendors } = useVendors()

  const { data, loading, error } = useQuery<any>('last_purchase_rate', () => {
    let q = supabase.from('v_last_purchase_rate').select('*')
    if (site) q = q.eq('site_id', site)
    if (vendor) q = q.eq('vendor_id', vendor)
    return q.order('approved_on', { ascending: false }).limit(1000)
  }, [site, vendor], { idField: 'po_id', cacheable: false })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((r: any) =>
      (r.item_name ?? '').toLowerCase().includes(t) ||
      (r.item_code ?? '').toLowerCase().includes(t) ||
      (r.vendor_name ?? '').toLowerCase().includes(t) ||
      (r.site_name ?? '').toLowerCase().includes(t))
  }, [data, term])

  const { sort, toggle } = useSort('approved_on')
  const sorted = useMemo(() => sortRows(rows, sort, {
    unit_rate: 'number', quantity: 'number', approved_on: 'date', days_ago: 'number',
    gst_rate_percent: 'number',
  }), [rows, sort])

  // 0212: when a rate counts as old is a System Setting (180 days until changed).
  const { staleRateDays } = useCompanySettings()
  const ageText = staleRateDays === 180 ? '6 months' : `${staleRateDays} days`

  if (!can(role, 'viewRates')) {
    return <NotPermitted what="see purchase rates" who="Procurement and the Head of Stores" />
  }

  const materialsCovered = new Set(rows.map((r: any) => r.material_id)).size
  const stale = rows.filter((r: any) => Number(r.days_ago) > staleRateDays)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Rate records" value={rows.length} icon={<IndianRupee className="h-5 w-5" />} />
        <Stat label="Materials covered" value={materialsCovered} />
        <Stat label={`Older than ${ageText}`} value={stale.length}
              tone={stale.length ? 'warn' : 'good'} sub="Worth re-quoting" />
        <Stat label="Vendors" value={new Set(rows.map((r: any) => r.vendor_id)).size} />
      </div>

      <Card
        title="Last purchase rate"
        subtitle="What we last agreed to pay for each material, at each site, from each vendor — captured when a purchase order is approved"
        actions={
          <button className="btn-secondary btn-sm" onClick={() => exportCsv(sorted)}>
            <Download className="h-4 w-4" /> Export
          </button>
        }
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search material, vendor or site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="All vendors"
                  options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading label="Loading rates…" />}
        {error && <div className="p-4"><EmptyState title="Could not load rates" message={error} /></div>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            icon={<IndianRupee className="h-9 w-9" />}
            title="No rates recorded yet"
            message="This table fills itself as purchase orders are approved. Approve one and it will appear here."
          />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="item_name" sort={sort} onSort={toggle}>Material</Th>
                  <Th id="item_code" sort={sort} onSort={toggle}>Code</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th>
                  <Th id="unit_rate" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="purchase_uom" sort={sort} onSort={toggle}>Per</Th>
                  <Th id="gst_rate_percent" sort={sort} onSort={toggle} className="num" align="right">GST %</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Quantity</Th>
                  <Th id="approved_on" sort={sort} onSort={toggle}>Agreed on</Th>
                  <Th id="days_ago" sort={sort} onSort={toggle} className="num" align="right">Age</Th>
                  <Th id="po_id" sort={sort} onSort={toggle}>On order</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => (
                  <tr key={`${r.material_id}|${r.site_id}|${r.vendor_id}`}
                      className={Number(r.days_ago) > staleRateDays ? 'bg-amber-50/40' : ''}>
                    <td className="text-sm font-medium">{r.item_name}</td>
                    <td className="font-mono text-xs text-slate-500">{r.item_code}</td>
                    <td className="text-xs">{r.site_name}</td>
                    <td className="max-w-[220px] truncate text-xs">{r.vendor_name}</td>
                    <td className="num text-sm font-semibold">{rupees(r.unit_rate)}</td>
                    <td className="text-xs text-slate-500">{r.purchase_uom}</td>
                    <td className="num text-xs">{r.gst_rate_percent}</td>
                    <td className="num text-xs text-slate-500">{qty(r.quantity)}</td>
                    <td className="text-xs">{ddmmyyyy(r.approved_on)}</td>
                    <td className={`num text-xs ${Number(r.days_ago) > staleRateDays ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                      {r.days_ago}d
                    </td>
                    <td>
                      <Link to={`/pos/${r.po_id}`} className="font-mono text-xs text-oval-700 hover:underline">
                        {r.po_id}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="How this is used">
        <ul className="space-y-1.5 text-sm text-slate-600">
          <li>
            <strong>It fills itself.</strong> Nothing is typed here. A row appears the moment the Head of
            Procurement approves a purchase order carrying that material.
          </li>
          <li>
            <strong>It comes back to you when you build a purchase order.</strong> Pick a material and the
            rate box offers the last agreed price, saying where it came from. Overwrite it whenever the
            market has moved — the suggestion is a starting point, not an instruction.
          </li>
          <li>
            <strong>Amber rows are older than {staleRateDays === 180 ? 'six months' : `${staleRateDays} days`}.</strong> Worth a fresh quotation before you rely
            on them.
          </li>
          <li>
            <strong>It also values the stock.</strong> The inventory export uses these rates to put an
            approximate figure against what is sitting at each site.
          </li>
        </ul>
      </Card>
    </div>
  )
}

/** Straight to CSV — this table is small and people want it in Excel. */
function exportCsv(rows: any[]) {
  const head = ['Material', 'Code', 'Site', 'Vendor', 'Rate', 'Per', 'GST %', 'Quantity', 'Agreed on', 'Age (days)', 'Purchase order']
  const body = rows.map((r) => [
    r.item_name, r.item_code, r.site_name, r.vendor_name, r.unit_rate, r.purchase_uom,
    r.gst_rate_percent, r.quantity, r.approved_on, r.days_ago, r.po_id,
  ])
  const csv = [head, ...body]
    .map((line) => line.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `last_purchase_rates_${localIso()}.csv`
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 4000)
}
